import { useCallback, useEffect, useState } from 'react';
import { mountGuideService } from '../services/api';
import type {
    MountGuideHome,
    MountGuideMountDetail,
    MountGuideRegionDetail,
} from '../types';
import {
    getCachedMountGuideHome,
    getCachedMountGuideMount,
    getCachedMountGuideRegion,
    isMountGuideHomeFresh,
    isMountGuideMountFresh,
    isMountGuideRegionFresh,
    setCachedMountGuideHome,
    setCachedMountGuideMount,
    setCachedMountGuideRegion,
} from '../utils/mountGuideCache';

type LoadState<T> = {
    data: T | null;
    loading: boolean;
    error: string | null;
};

function useMountGuideLoader<T>(
    key: string | undefined,
    getCached: (key: string) => T | null,
    isFresh: (key: string) => boolean,
    setCached: (key: string, data: T) => void,
    fetcher: (key: string) => Promise<T>,
    fallback: T | null,
    errorMessage: string
): LoadState<T> {
    const [data, setData] = useState<T | null>(() =>
        key ? getCached(key) ?? fallback : fallback
    );
    const [loading, setLoading] = useState(() => !(key && getCached(key)));
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!key) return;

        const cached = getCached(key);
        if (cached) {
            setData(cached);
            setError(null);
            setLoading(false);
        } else {
            setLoading(true);
        }

        if (cached && isFresh(key)) return;

        try {
            const res = await fetcher(key);
            setCached(key, res);
            setData(res);
            setError(null);
        } catch {
            if (!cached) {
                setData(fallback);
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    }, [key, getCached, isFresh, setCached, fetcher, fallback, errorMessage]);

    useEffect(() => {
        load();
    }, [load]);

    return { data, loading, error };
}

export function useMountGuideHome(): LoadState<MountGuideHome> {
    const empty: MountGuideHome = { sections: [], ungroupedRegions: [] };

    const [data, setData] = useState<MountGuideHome | null>(() => getCachedMountGuideHome() ?? null);
    const [loading, setLoading] = useState(() => !getCachedMountGuideHome());
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const cached = getCachedMountGuideHome();
        if (cached) {
            setData(cached);
            setLoading(false);
        } else {
            setLoading(true);
        }

        if (isMountGuideHomeFresh()) return;

        let cancelled = false;
        (async () => {
            try {
                const res = await mountGuideService.getHome();
                if (cancelled) return;
                setCachedMountGuideHome(res);
                setData(res);
                setError(null);
            } catch {
                if (cancelled) return;
                if (!cached) {
                    setData(empty);
                    setError('load_failed');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    return { data, loading, error };
}

export function useMountGuideRegion(
    regionSlug: string | undefined,
    notFoundMessage: string
): LoadState<MountGuideRegionDetail> {
    return useMountGuideLoader(
        regionSlug,
        getCachedMountGuideRegion,
        isMountGuideRegionFresh,
        setCachedMountGuideRegion,
        (slug) => mountGuideService.getRegion(slug),
        null,
        notFoundMessage
    );
}

export function useMountGuideMount(mountSlug: string | undefined): LoadState<MountGuideMountDetail> {
    return useMountGuideLoader(
        mountSlug,
        getCachedMountGuideMount,
        isMountGuideMountFresh,
        setCachedMountGuideMount,
        (slug) => mountGuideService.getMount(slug),
        null,
        'not_found'
    );
}
